import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import AuthPage from "@/pages/AuthPage";
import ProfilePage from "@/pages/ProfilePage";
import SuggestionsPage from "@/pages/SuggestionsPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSetup from "@/pages/admin/AdminSetup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("gcsr_token"));
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Fetch cart
          const cartRes = await axios.get(`${API}/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCart(cartRes.data);
        } catch {
          localStorage.removeItem("gcsr_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch {
        // Already seeded or error
      }
    };
    seedData();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("gcsr_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    // Fetch cart after login
    const cartRes = await axios.get(`${API}/cart`, {
      headers: { Authorization: `Bearer ${response.data.token}` }
    });
    setCart(cartRes.data);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("gcsr_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    setCart({ items: [], total: 0 });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("gcsr_token");
    setToken(null);
    setUser(null);
    setCart({ items: [], total: 0 });
  };

  const addToCart = async (productId, quantity, size, color) => {
    if (!token) return null;
    const response = await axios.post(
      `${API}/cart/add`,
      { product_id: productId, quantity, size, color },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCart(response.data.cart);
    return response.data;
  };

  const removeFromCart = async (productId, size, color) => {
    if (!token) return null;
    const params = new URLSearchParams();
    if (size) params.append("size", size);
    if (color) params.append("color", color);
    const response = await axios.delete(
      `${API}/cart/remove/${productId}?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCart(response.data.cart);
    return response.data;
  };

  const updateCartItem = async (productId, quantity, size, color) => {
    if (!token) return null;
    const response = await axios.put(
      `${API}/cart/update`,
      { product_id: productId, quantity, size, color },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCart(response.data.cart);
    return response.data;
  };

  const clearCart = async () => {
    if (!token) return null;
    await axios.delete(`${API}/cart/clear`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCart({ items: [], total: 0 });
  };

  const refreshCart = async () => {
    if (!token) return;
    const cartRes = await axios.get(`${API}/cart`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCart(cartRes.data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateCartItem, clearCart, refreshCart }}>
        <BrowserRouter>
          <div className="min-h-screen bg-[#030303] flex flex-col">
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/shop/:category" element={<ShopPage />} />
                <Route path="/product/:id" element={<ProductPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={user ? <CheckoutPage /> : <Navigate to="/auth" />} />
                <Route path="/auth" element={user ? <Navigate to="/" /> : <AuthPage />} />
                <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/auth" />} />
                <Route path="/suggestions" element={<SuggestionsPage />} />
                {/* Admin Routes */}
                <Route path="/admin/setup" element={<AdminSetup />} />
                <Route path="/admin" element={user?.is_admin ? <AdminDashboard /> : <Navigate to="/" />} />
                <Route path="/admin/products" element={user?.is_admin ? <AdminProducts /> : <Navigate to="/" />} />
                <Route path="/admin/products/new" element={user?.is_admin ? <AdminProductForm /> : <Navigate to="/" />} />
                <Route path="/admin/products/:id/edit" element={user?.is_admin ? <AdminProductForm /> : <Navigate to="/" />} />
                <Route path="/admin/orders" element={user?.is_admin ? <AdminOrders /> : <Navigate to="/" />} />
                <Route path="/admin/settings" element={user?.is_admin ? <AdminSettings /> : <Navigate to="/" />} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
